/**
 * Toast Container Component
 * Renders toast notifications from the notification store
 */

import { useEffect } from 'react';
import { useTheme } from '@/theme';
import { useTranslation } from '@/i18n';
import { useNotificationStore, type Toast } from '@/store';

/** Toast type colors and icons */
const getToastStyles = (type: Toast['type'], colors: Record<string, string>) => {
  switch (type) {
    case 'success':
      return {
        backgroundColor: colors.success,
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        ),
      };
    case 'error':
      return {
        backgroundColor: colors.error,
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        ),
      };
    case 'warning':
      return {
        backgroundColor: colors.warning,
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        ),
      };
    case 'info':
    default:
      return {
        backgroundColor: colors.brandAccent,
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        ),
      };
  }
};

/** Individual Toast Item */
const ToastItem = ({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = getToastStyles(toast.type, theme.colors);

  // Auto-dismiss after duration
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(onDismiss, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, onDismiss]);

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 16px',
        backgroundColor: theme.colors.backgroundCard,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadows.dropdown,
        borderLeft: `4px solid ${styles.backgroundColor}`,
        minWidth: '320px',
        maxWidth: '420px',
        animation: 'slideInRight 0.3s ease-out',
      }}
    >
      {/* Icon */}
      <div
        style={{
          flexShrink: 0,
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: styles.backgroundColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {styles.icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: theme.typography.fontSizeBase,
            fontWeight: theme.typography.fontWeightSemibold,
            color: theme.colors.textPrimary,
            marginBottom: toast.message ? '4px' : 0,
          }}
        >
          {toast.title}
        </div>
        {toast.message && (
          <div
            style={{
              fontSize: theme.typography.fontSizeSm,
              color: theme.colors.textSecondary,
              lineHeight: 1.4,
              whiteSpace: 'pre-line',
            }}
          >
            {toast.message}
          </div>
        )}
      </div>

      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        aria-label={t('Common:DismissNotification', { defaultValue: 'Dismiss notification' })}
        style={{
          flexShrink: 0,
          padding: '4px',
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: theme.borderRadius.sm,
          cursor: 'pointer',
          color: theme.colors.textMuted,
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = theme.colors.textPrimary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = theme.colors.textMuted;
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

/** Toast Container - renders all toasts */
export const ToastContainer = () => {
  const { toasts, removeToast } = useNotificationStore();
  const { t } = useTranslation();

  if (toasts.length === 0) return null;

  return (
    <>
      {/* CSS Animation */}
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div
        aria-live="polite"
        aria-label={t('Notifications', { defaultValue: 'Notifications' })}
        style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: '12px',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => (
          <div key={toast.id} style={{ pointerEvents: 'auto' }}>
            <ToastItem toast={toast} onDismiss={() => removeToast(toast.id)} />
          </div>
        ))}
      </div>
    </>
  );
};

export default ToastContainer;
